import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import cloneDeep from 'lodash/cloneDeep';

import {
  createPage as apiCreatePage,
  deletePage as apiDeletePage,
  updatePage as apiUpdatePage,
} from '../api/build-a-dash-api';
import type { Page } from '../components/PageConfigProvider/types';
import { pageQueryKeys } from './usePagesQuery';

function optimisticUpdateHelper(
  queryClient: QueryClient,
  updater: (pages: Page[]) => Page[]
) {
  const prevPages = queryClient.getQueryData<Page[]>(pageQueryKeys.lists());
  if (prevPages) {
    queryClient.setQueryData<Page[]>(
      pageQueryKeys.lists(),
      updater(cloneDeep(prevPages))
    );
  }
  return prevPages;
}

export function usePageMutations() {
  const queryClient = useQueryClient();

  /** CREATE */
  const createPage = useMutation({
    mutationFn: apiCreatePage,
    onMutate: async (newPage: Page) => {
      await queryClient.cancelQueries({ queryKey: pageQueryKeys.lists() });
      const prevPages = optimisticUpdateHelper(queryClient, (pages) => [
        ...pages,
        newPage,
      ]);
      return { prevPages };
    },
    onError: (_err, _newPage, context) => {
      if (context?.prevPages) {
        queryClient.setQueryData(pageQueryKeys.lists(), context.prevPages);
      }
    },
  });

  /** UPDATE */
  const updatePage = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Page> }) =>
      apiUpdatePage(id, data),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: pageQueryKeys.lists() });
      const prevPages = optimisticUpdateHelper(queryClient, (pages) =>
        pages.map((p) =>
          p.id === variables.id ? ({ ...p, ...variables.data } as Page) : p
        )
      );
      return { prevPages };
    },
    onError: (_err, _variables, context) => {
      if (context?.prevPages) {
        queryClient.setQueryData(pageQueryKeys.lists(), context.prevPages);
      }
    },
  });

  /** DELETE */
  const deletePage = useMutation({
    mutationFn: (id: string) => apiDeletePage(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: pageQueryKeys.lists() });
      const prevPages = optimisticUpdateHelper(queryClient, (pages) =>
        pages.filter((p) => p.id !== id)
      );
      return { prevPages };
    },
    onError: (_err, _id, context) => {
      if (context?.prevPages) {
        queryClient.setQueryData(pageQueryKeys.lists(), context.prevPages);
      }
    },
  });

  return {
    handleCreatePage: createPage,
    handleUpdatePage: updatePage,
    handleDeletePage: deletePage,
  };
}
